import React, { useState, useEffect } from 'react';
import { Box, Grid, List, ListItemButton, ListItemAvatar, ListItemText, Avatar, Typography, Divider, CircularProgress, TextField, InputAdornment, Paper } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import ChatWindow from './ChatWindow'; 
import { getAiConversations } from '../../../../controllers/company/ai/AiChatController';



export default function AiOperationsCenter() {
    const [chatList, setChatList] = useState([]);
    const [selectedChat, setSelectedChat] = useState(null);
    const [isListLoading, setIsListLoading] = useState(false);
    
    // Estado para el buscador de la lista de clientes
    const [listSearch, setListSearch] = useState('');

    useEffect(() => {
        fetchChatList(false); 

        const listIntervalId = setInterval(() => {
            fetchChatList(true); 
        }, 10000);

        return () => clearInterval(listIntervalId);
    }, []);

    const fetchChatList = async (isBackground = false) => {
        if (!isBackground) setIsListLoading(true);
        try {
            const response = await getAiConversations();
            if (response && response.data) {
                setChatList(response.data.chats || response.data);
                
                if (selectedChat) {
                    const updatedChat = (response.data.chats || response.data).find(c => c.id === selectedChat.id);
                    if (updatedChat) setSelectedChat(updatedChat);
                }
            }
        } catch (error) {
            console.error("Error cargando la lista de chats:", error);
        } finally {
            if (!isBackground) setIsListLoading(false);
        }
    };

    // Filtrado de la lista de chats
    const filteredChats = chatList.filter(chat => 
        (chat.display_name || chat.phone || '').toLowerCase().includes(listSearch.toLowerCase())
    );

    return (
        // Contenedor principal ajustado: Paper con borde y altura controlada para no chocar con los KPIs
        <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, height: '650px', display: 'flex', overflow: 'hidden', mt: 2 }}>
            <Grid container sx={{ height: '100%' }}>
                
                {/* Panel Izquierdo: Lista de Chats y Buscador */}
                <Grid item xs={12} md={4} sx={{ borderRight: '1px solid #e0e0e0', height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#fff' }}>
                    
                    {/* Buscador de Clientes */}
                    <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Buscar cliente o teléfono..."
                            value={listSearch}
                            onChange={(e) => setListSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" sx={{ color: '#9ca3af' }} />
                                    </InputAdornment>
                                ),
                                sx: { 
                                    borderRadius: 2, 
                                    backgroundColor: '#f9fafb',
                                    '& fieldset': { borderColor: 'transparent' },
                                    '&:hover fieldset': { borderColor: '#e5e7eb' },
                                    '&.Mui-focused fieldset': { borderColor: '#3b82f6' } 
                                }
                            }}
                        />
                    </Box>

                    {/* Lista con Scroll */}
                    <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                        {isListLoading ? (
                            <Box display="flex" justifyContent="center" alignItems="center" mt={4}>
                                <CircularProgress size={24} sx={{ color: '#111827' }} />
                            </Box>
                        ) : filteredChats.length === 0 ? (
                            <Box sx={{ p: 3, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                    {listSearch ? 'No se encontraron clientes.' : 'No hay conversaciones activas.'}
                                </Typography>
                            </Box>
                        ) : (
                            <List disablePadding>
                                {filteredChats.map((chat) => {
                                    const isSelected = selectedChat?.id === chat.id;
                                    return (
                                        <React.Fragment key={chat.id}>
                                            <ListItemButton 
                                                selected={isSelected}
                                                onClick={() => setSelectedChat(chat)}
                                                sx={{
                                                    p: 2,
                                                    backgroundColor: isSelected ? '#f3f4f6' : 'transparent',
                                                    '&:hover': { backgroundColor: '#f9fafb' }
                                                }}
                                            >
                                                <ListItemAvatar>
                                                    <Avatar sx={{ bgcolor: '#e5e7eb', color: '#9ca3af' }}>
                                                        <PersonIcon />
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={
                                                        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                                                            <Typography variant="body1" fontWeight="600" sx={{ flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                {chat.display_name || chat.phone}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', ml: 1 }}>
                                                                {chat.updated_time || ''}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    secondary={
                                                        <Typography variant="body2" color="text.secondary" noWrap>
                                                            {chat.last_message || 'Sin mensajes aún...'}
                                                        </Typography>
                                                    }
                                                />
                                            </ListItemButton>
                                            <Divider component="li" />
                                        </React.Fragment>
                                    );
                                })}
                            </List>
                        )}
                    </Box>
                </Grid>

                {/* Panel Derecho: Ventana de Chat */}
                <Grid item xs={12} md={8} sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#f9fafb' }}>
                    {selectedChat ? (
                        <ChatWindow chat={selectedChat} />
                    ) : (
                        <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <Typography variant="body1" color="text.secondary">
                                Selecciona una conversación para gestionar los mensajes del Agente
                            </Typography>
                        </Box>
                    )}
                </Grid>

            </Grid>
        </Paper>
    );
}